import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { MOIS, monthKey } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export const EmployeeDialog = ({ open, onClose, companyId, year, employee, onSaved }) => {
  const [form, setForm] = useState({ nom: "", prenom: "", poste: "", apprenti: false });
  const [months, setMonths] = useState({});
  const [fill, setFill] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ nom: employee?.nom || "", prenom: employee?.prenom || "", poste: employee?.poste || "", apprenti: !!employee?.apprenti });
    setMonths(employee?.remunerations || {});
    setFill("");
  }, [open, employee]);

  const setMonth = (m, v) => setMonths((p) => ({ ...p, [monthKey(year, m)]: v === "" ? 0 : Number(v) }));
  const applyFill = () => {
    if (fill === "") return;
    const next = { ...months };
    for (let m = 1; m <= 12; m++) next[monthKey(year, m)] = Number(fill);
    setMonths(next);
  };
  const total = Array.from({ length: 12 }, (_, i) => Number(months[monthKey(year, i + 1)] || 0)).reduce((a, b) => a + b, 0);

  const save = async () => {
    if (!form.nom.trim()) return toast.error("Le nom est obligatoire");
    setSaving(true);
    try {
      const payload = { ...form, remunerations: months };
      if (employee) await api.put(`/companies/${companyId}/employees/${employee.id}`, payload);
      else await api.post(`/companies/${companyId}/employees`, payload);
      toast.success(employee ? "Salarié mis à jour" : "Salarié ajouté");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="employee-dialog">
        <DialogHeader><DialogTitle className="font-heading">{employee ? "Modifier le salarié" : "Nouveau salarié"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Nom *</Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="employee-nom-input" /></div>
          <div><Label>Prénom</Label><Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="employee-prenom-input" /></div>
          <div><Label>Poste</Label><Input value={form.poste} onChange={(e) => setForm({ ...form, poste: e.target.value })} data-testid="employee-poste-input" /></div>
        </div>
        <div className="flex items-center gap-3 rounded-md border border-border p-3 bg-muted/50">
          <Switch checked={form.apprenti} onCheckedChange={(v) => setForm({ ...form, apprenti: v })} data-testid="employee-apprenti-switch" />
          <div className="text-sm">
            <div className="font-medium text-foreground">Apprenti (contrat d'apprentissage)</div>
            <div className="text-xs text-muted-foreground">Exonération art. 231 bis I CGI : totale si l'effectif hors apprentis ≤ 10, sinon 11 % du SMIC mensuel (20 % en DOM).</div>
          </div>
        </div>
        <div className="mt-2">
          <div className="flex items-end justify-between mb-2">
            <Label>Rémunérations brutes {year}</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min="0" placeholder="Brut mensuel" className="w-36 h-8 font-mono" value={fill} onChange={(e) => setFill(e.target.value)} data-testid="employee-gross-salary-input" />
              <Button type="button" variant="outline" size="sm" onClick={applyFill} data-testid="employee-fill-all-button">Appliquer aux 12 mois</Button>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {MOIS.map((label, i) => (
              <div key={label}>
                <span className="text-[11px] text-muted-foreground">{label}</span>
                <Input type="number" min="0" className="h-8 font-mono" value={months[monthKey(year, i + 1)] ?? ""} onChange={(e) => setMonth(i + 1, e.target.value)} data-testid={`employee-month-input-${i + 1}`} />
              </div>
            ))}
          </div>
          <div className="text-right text-sm text-muted-foreground mt-2">Total annuel : <span className="font-mono font-semibold text-foreground" data-testid="employee-annual-total">{total.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="employee-save-button">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
